import api from './api';

export interface ManagerNoteBackend {
  id: string;
  managerId: string;
  managerName: string;
  employeeId: string;
  content: string;
  created: string;
  updated: string;
}

export interface ManagerNote {
  id: string;
  userId: string;
  authorId: string;
  authorName: string;
  content: string;
  createdAt: string;
}

export interface CreateManagerNoteDto {
  content: string;
}

interface SpringPageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface PaginatedManagerNotesResponse {
  notes: ManagerNote[];
  total: number;
  page: number;
  totalPages: number;
}

function mapBackendNote(note: ManagerNoteBackend): ManagerNote {
  return {
    id: note.id,
    userId: note.employeeId,
    authorId: note.managerId,
    authorName: note.managerName ?? '',
    content: note.content,
    createdAt: note.created,
  };
}

/**
 * Manager Notes Service
 * Centralizes all manager notes-related API calls
 */
class ManagerNotesService {
  /**
   * Create a new manager note
   */
  async create(userId: string, data: CreateManagerNoteDto): Promise<ManagerNote> {
    const response = await api.post<ManagerNoteBackend>(`/members/${userId}/manager-notes`, data);
    return mapBackendNote(response.data);
  }

  /**
   * Get manager notes for a user with pagination
   */
  async getByUserId(
    userId: string,
    page: number = 0,
    size: number = 10
  ): Promise<PaginatedManagerNotesResponse> {
    const response = await api.get<SpringPageResponse<ManagerNoteBackend>>(
      `/members/${userId}/manager-notes?page=${page}&size=${size}`
    );
    const data = response.data;
    return {
      notes: data.content.map(mapBackendNote),
      total: data.totalElements,
      page: data.number,
      totalPages: data.totalPages,
    };
  }
}

// Export singleton instance
export const managerNotesService = new ManagerNotesService();
